import { useFormik } from 'formik';
import { pick } from 'lodash-es';
import { ChangeEvent, useCallback, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGetFeatureFlagsQuery } from '../../../../api/features';
import { useGetBusinessTypesQuery } from '../../../../api/reference-data';
import { businessInfoVerifyValidationSchema } from '../../../../config/page-validation-schema';
import { FormikEinField } from '../../../../general/inputs/formik-ein-field';
import { FormikSelectField } from '../../../../general/inputs/formik-select-field';
import { FormikTextField } from '../../../../general/inputs/formik-text-field';
import { RootState } from '../../../../store';
import { AddressFormFields } from '../../../address/components/address-form-fields';
import { AddressFormFieldsWithAutocomplete } from '../../../address/components/address-form-fields-with-autocomplete';
import { SubmitWorkflowWithBack } from '../../../workflow/components/submit-workflow-with-back';
import {
  businessPageVerifyKeys,
  BusinessPageVerifyPayload,
  saveBusinessInfoVerify,
} from '../../reducers/business-info';

const selector = (state: RootState) => {
  const {
    businessInfo: { info },
    addressLookup: { loading: addressLoading },
    nextStep: { loading },
  } = state;
  return {
    saving: loading || addressLoading,
    businessInfo: pick(info, businessPageVerifyKeys),
  };
};

const validationSchema = businessInfoVerifyValidationSchema;

const BusinessInfoVerifyForm: FC = () => {
  const { data: featureFlags } = useGetFeatureFlagsQuery();
  const { data: businessTypes } = useGetBusinessTypesQuery();

  const dispatch = useDispatch();
  const { businessInfo, saving } = useSelector(selector);

  const formik = useFormik({
    initialValues: {
      ...businessInfo,
    },
    validationSchema,
    onSubmit: (values: BusinessPageVerifyPayload) => {
      dispatch(saveBusinessInfoVerify({ ...values, name: values.name.trim() }));
    },
  });

  const formikSetFieldValue = formik.setFieldValue;
  const onChangeEin = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const ein = e.target.value.replace(/\D/g, '');
      void formikSetFieldValue('ein', ein);
    },
    [formikSetFieldValue],
  );

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <FormikTextField formik={formik} id="name" name="name" label="Company Legal Name" />
        <FormikEinField name="ein" id="ein" label="Company EIN" formik={formik} onChange={onChangeEin} />
        <FormikSelectField
          formik={formik}
          name="businessType"
          id="businessType"
          options={businessTypes ?? []}
          label="Business Type"
          placeholder="Business Type"
        />
        {!featureFlags?.qfEnableAddressAutocomplete && <AddressFormFields formik={formik} />}
        {featureFlags?.qfEnableAddressAutocomplete && <AddressFormFieldsWithAutocomplete formik={formik} />}
        <SubmitWorkflowWithBack type="submit" progress={saving} sx={{ mt: 3 }}>
          Next
        </SubmitWorkflowWithBack>
      </form>
    </>
  );
};

export { BusinessInfoVerifyForm };
